import styled from 'styled-components'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: 0px ${({ theme }) => theme.spacing.large};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.default};
    gap: ${({ theme }) => theme.spacing.default};
  }
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

export const HeaderIcon = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
`

export const HeaderText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.micro};
`

export const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const Subtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

export const VoiceRecordingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.xlarge};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`

export const RecordButton = styled.button<{ $isRecording?: boolean }>`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  background: ${({ theme, $isRecording }) =>
    $isRecording ? theme.colors.danger : theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.md};

  &:hover {
    transform: scale(1.05);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

export const RecordingIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

export const RecordingDuration = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.danger};
  font-variant-numeric: tabular-nums;
`

export const RecordingHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-align: center;
`

export const IntroContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.large};
  padding: ${({ theme }) => theme.spacing.xlarge};
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
`

export const IntroIcon = styled.div`
  font-size: 64px;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`

export const IntroTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const IntroDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`

export const IntroInstructionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${({ theme }) => theme.spacing.medium} 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  text-align: left;
  width: 100%;
`

export const IntroInstruction = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;

  &:before {
    content: '✓';
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    background: ${({ theme }) => theme.colors.successLight};
    color: ${({ theme }) => theme.colors.success};
    border-radius: ${({ theme }) => theme.borderRadius.circle};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    font-size: ${({ theme }) => theme.typography.fontSize.micro};
  }
`

export const IntroActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.medium};

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`

export const ActionsBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.small};

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`

export const StatusMessage = styled.div<{
  $type?: 'info' | 'success' | 'error' | 'warning'
}>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.default};
  background: ${({ theme, $type }) => {
    switch ($type) {
      case 'success':
        return `${theme.colors.success}10`
      case 'error':
        return `${theme.colors.danger}10`
      case 'warning':
        return `${theme.colors.warning}10`
      default:
        return `${theme.colors.primary}10`
    }
  }};
  border: 1px solid
    ${({ theme, $type }) => {
      switch ($type) {
        case 'success':
          return theme.colors.success
        case 'error':
          return theme.colors.danger
        case 'warning':
          return theme.colors.warning
        default:
          return theme.colors.primary
      }
    }};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  color: ${({ theme, $type }) => {
    switch ($type) {
      case 'success':
        return theme.colors.success
      case 'error':
        return theme.colors.danger
      case 'warning':
        return theme.colors.warning
      default:
        return theme.colors.primary
    }
  }};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.5;
`

export const StatusIcon = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  flex-shrink: 0;
`

export const StatusText = styled.div`
  flex: 1;
`

export const ResultsPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  animation: fadeIn 0.4s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

export const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding-bottom: ${({ theme }) => theme.spacing.default};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

export const ResultsTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ResultsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.default};
`

export const ResultSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const ResultLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const ResultValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const EditableDescriptionWrapper = styled.div`
  position: relative;
  width: 100%;
`

export const ExtractedAttributesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.default};
  padding: ${({ theme }) => theme.spacing.default};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.small};
  }
`

export const AttributeCategory = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const CategoryTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  padding-bottom: ${({ theme }) => theme.spacing.xsmall};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const AttributesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const AttributeItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

export const AttributeKey = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: capitalize;
`

export const AttributeValue = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

export const ResultsActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.small};
  padding-top: ${({ theme }) => theme.spacing.default};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
  padding: ${({ theme }) => theme.spacing.xlarge};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`

export const LoadingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};
`

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${({ theme }) => theme.colors.background.secondary};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const LoadingText = styled.div`
  flex: 1;
`

export const LoadingTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

export const LoadingSubtitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const SkeletonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.default};
`

export const SkeletonLine = styled.div<{ $width?: string; $height?: string }>`
  width: ${({ $width = '100%' }) => $width};
  height: ${({ $height = '16px' }) => $height};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.background.secondary} 25%,
    ${({ theme }) => theme.colors.background.tertiary} 50%,
    ${({ theme }) => theme.colors.background.secondary} 75%
  );
  background-size: 200% 100%;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

export const SuccessAnimation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.success}20;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  font-size: 32px;
  animation: scaleIn 0.3s ease-out;

  @keyframes scaleIn {
    0% {
      transform: scale(0);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`
